import React from "react";
import { ThemedSiderV2 } from "@refinedev/mui";
import { useBranch } from "../../provider/BranchProvider";
import CustomTitle from "./CustomTitle";
import { BranchAuthenticationType } from "../../types/branch";

/**
 * CustomSider with menu filter for branch-aware visibility.
 *
 * Visibility rules:
 * - Admin branch (no branch selected): show ONLY sections flagged with meta.adminBranch (and their children).
 * - Non-admin branches: hide admin-only sections (meta.adminBranch === true).
 * - Blockchain-only entries (meta.blockchainBranch === true) are visible only if the current branch is a BLOCKCHAIN branch.
 * - Containers/groups with no visible children are removed.
 */
export const CustomSider = () => {
  const branch = useBranch();

  type MenuMeta = { adminBranch?: boolean; blockchainBranch?: boolean };
  type BranchMenuContext = {
    isAdminBranch: boolean;
    isBlockchainBranch: boolean;
  };

  const ctx: BranchMenuContext = {
    isAdminBranch: !branch,
    isBlockchainBranch:
      branch?.authenticationType === BranchAuthenticationType.BLOCKCHAIN,
  };

  // Decide if a node with given meta should be included at this level.
  const shouldInclude = (
    meta: MenuMeta,
    context: BranchMenuContext,
    inAdminSection: boolean
  ) => {
    const adminOnly = !!meta.adminBranch;
    const blockchainOnly = !!meta.blockchainBranch;
    // Admin branch: only nodes that live inside an admin section are allowed
    if (context.isAdminBranch && !inAdminSection && !adminOnly) return false;
    // Non-admin branches: exclude admin-only nodes entirely
    if (!context.isAdminBranch && adminOnly) return false;
    // Blockchain-gated nodes: only on blockchain branches (non-admin context)
    if (!context.isAdminBranch && blockchainOnly && !context.isBlockchainBranch)
      return false;
    return true;
  };

  // Recursively prune the menu tree produced by ThemedSiderV2 render prop
  const pruneMenu = (
    node: React.ReactNode,
    context: BranchMenuContext,
    inAdminSection: boolean = false
  ): React.ReactNode => {
    if (Array.isArray(node)) {
      return node
        .map((n) => pruneMenu(n, context, inAdminSection))
        .filter(Boolean) as React.ReactNode[];
    }

    if (!React.isValidElement(node)) return node;

    const res = (node.props as any)?.params?.resource as
      | { meta?: MenuMeta }
      | undefined;
    const meta: MenuMeta = res?.meta ?? {};

    const adminOnly = !!meta.adminBranch;
    const nextInAdminSection = inAdminSection || adminOnly;

    if (!shouldInclude(meta, ctx, nextInAdminSection)) return null;

    const rawChildren = (node.props as any)?.children as React.ReactNode;
    const filteredChildren = pruneMenu(
      rawChildren,
      context,
      nextInAdminSection
    );

    // Prune empty containers/groups
    if (
      rawChildren &&
      Array.isArray(filteredChildren) &&
      filteredChildren.length === 0
    ) {
      return null;
    }

    if (rawChildren !== filteredChildren) {
      return React.cloneElement(node, undefined, filteredChildren as any);
    }

    return node;
  };

  return (
    <ThemedSiderV2
      Title={CustomTitle}
      render={({ items, logout }) => {
        const pruned = pruneMenu(items, ctx);
        return (
          <>
            {pruned}
            {logout}
          </>
        );
      }}
    />
  );
};
